/**
 * Перенос `presentation/providers/person_giff_provider.dart`.
 *
 * В Dart провайдер отдавал `Uint8List`: `DefaultCacheManager().downloadFile`
 * скачивал анимацию аватара, клал её в файловый кеш и возвращал байты, а
 * `Image.memory` их рисовал. В браузере файлового кеша нет и байты картинке
 * не отдашь — нужен `blob:`-адрес для `<img src>`.
 *
 * Поэтому обязанности разделены:
 *   - скачанный `Blob` лежит в модульном кеше по адресу — это то, что
 *     `flutter_cache_manager` давал даром: возврат к списку аватаров не
 *     скачивает анимации заново;
 *   - `blob:`-ссылка своя у каждого монтирования и отзывается при
 *     размонтировании, иначе браузер держит копию в памяти до перезагрузки
 *     страницы.
 */

import { useEffect, useState } from 'react';

import {
  ASYNC_LOADING,
  type AsyncValue,
  asyncData,
  asyncError,
} from '@/presentation/stores/asyncValue';
import { toRequestUrl } from '@/utils/proxyUrl';

/**
 * Сколько анимаций держим скачанными. Аватаров на планшете единицы,
 * запас взят с потолка — кеш не должен расти без предела.
 */
const GIFF_CACHE_LIMIT = 16;

/** Адрес -> обещание с байтами. Обещание, а не `Blob`: две карточки с одним
 *  адресом должны разделить одну загрузку, а не запустить две. */
const giffCache = new Map<string, Promise<Blob>>();

async function downloadGiff(url: string): Promise<Blob> {
  // Тот же прокси, что и у остальных запросов: адрес ноды аватара
  // произвольный, CORS-заголовков от него ждать нельзя.
  const response = await fetch(toRequestUrl(url));
  if (!response.ok) {
    throw new Error(`person giff ${response.status} ${response.statusText}: ${url}`);
  }
  return await response.blob();
}

/** Вытесняет самые старые записи — `Map` хранит их в порядке вставки. */
function evictOverflow(): void {
  for (const url of giffCache.keys()) {
    if (giffCache.size <= GIFF_CACHE_LIMIT) return;
    giffCache.delete(url);
  }
}

function loadGiff(url: string): Promise<Blob> {
  const cached = giffCache.get(url);
  if (cached !== undefined) return cached;

  const pending = downloadGiff(url);
  giffCache.set(url, pending);
  // Неудачную загрузку в кеше не держим: следующий заход попробует снова.
  // Отказ обрабатывает вызывающий, здесь только уборка.
  void pending.catch(() => {
    giffCache.delete(url);
  });
  evictOverflow();
  return pending;
}

/**
 * `ref.watch(personGiffProvider(giff))`.
 *
 * `null` вместо адреса — `null` вместо состояния: в Dart карточка без
 * `giff` вообще не создавала провайдер (`giff == null ? null : …`), а хук
 * условным быть не может.
 */
export function usePersonGiff(url: string | null): AsyncValue<string> | null {
  const [state, setState] = useState<AsyncValue<string> | null>(
    url === null ? null : ASYNC_LOADING,
  );
  const [renderedUrl, setRenderedUrl] = useState<string | null>(url);
  if (renderedUrl !== url) {
    // Смена адреса — другой провайдер семейства: прошлая анимация на
    // экране остаться не должна.
    setRenderedUrl(url);
    setState(url === null ? null : ASYNC_LOADING);
  }

  useEffect(() => {
    if (url === null) return;
    let active = true;
    let objectUrl: string | null = null;
    void (async (): Promise<void> => {
      try {
        const blob = await loadGiff(url);
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setState(asyncData(objectUrl));
      } catch (error) {
        if (!active) return;
        setState(asyncError(error));
      }
    })();
    return () => {
      active = false;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return state;
}
